/**
 * Сессия скаута: вход по OTP, роли, выход.
 *
 * Вход двухшаговый, как в админке: телефон → код. Код приходит через
 * Telegram-бот (@aida_otp_bot) либо SMS — это решает бэкенд.
 *
 * ⚠ РОЛЕЙ ДВЕ, И ВТОРАЯ — НЕ УРЕЗАННАЯ ПЕРВАЯ.
 *
 * `scout` — свой сотрудник: пул диспетчера, статусы байка, фонарик, вывоз.
 * `partner_scout` — подрядчик, который делает ровно одно: переставляет
 * неправильно припаркованный байк и едет дальше. Ему закрыты пул, claim,
 * все Mark*, фонарик, точка высадки клиента и ремонтные центры — сервер
 * отвечает 403 STAFF_REQUIRED. Интерфейс обязан не предлагать того, что
 * гарантированно откажут: отказ доезжает до человека через очередь, спустя
 * полчаса, когда он уже на другом конце города.
 *
 * Роли исключают друг друга НАМЕРЕННО (`isPartner` требует отсутствия
 * `scout`): сотрудник с обеими ролями — это сотрудник, и урезать ему экран
 * не за что.
 *
 * Проверка роли по-прежнему ПРЕДУПРЕЖДАЕТ, а не блокирует вход: под `staff`
 * проходит всё, и разбор инцидентов ведётся из-под него.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { z } from 'zod'
import { ApiError, onSessionEnded, request } from './http'
import {
  clearTokens,
  decodeClaims,
  hydrateTokens,
  readTokens,
  writeTokens,
  type TokenPair,
} from './tokens'
import { clearOutbox } from './offline/outbox'
import { clearPersistedCache } from './offline/cache'
import { clearMarks } from './shift-log'
import { canOpenCollector, isCollectorOnly } from './collector'

/** public.proto RequestOTPReply — только request_id, никаких каналов доставки. */
const otpRequestReply = z.object({
  requestId: z.string().default(''),
})

/** public.proto VerifyOTPReply. */
const verifyReply = z.object({
  accessToken: z.string().default(''),
  refreshToken: z.string().default(''),
  expiresAt: z.string().default(''),
  firstLogin: z.boolean().default(false),
})

export type AuthState = {
  /** id пользователя из claim `sub`. Пусто — не вошли. */
  userId: string
  roles: string[]
  /** true, пока не выяснили, есть ли сохранённая сессия. */
  loading: boolean
  isScout: boolean
  /** Подрядчик: только перестановка. Строгое подмножество `scout`. */
  isPartner: boolean
  /** Сборщик без скаутских ролей — видит только режим сборщика (`lib/collector.ts`). */
  isCollectorOnly: boolean
  /** Может открыть режим сборщика: сам сборщик или staff/admin для проверки. */
  canCollect: boolean
  requestCode: (phone: string) => Promise<{ requestId: string }>
  verifyCode: (phone: string, code: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth вызван вне AuthProvider')
  return ctx
}

function claimsOf(pair: TokenPair | null): { userId: string; roles: string[] } {
  if (!pair) return { userId: '', roles: [] }
  const c = decodeClaims(pair.accessToken)
  return { userId: c?.sub ?? '', roles: c?.roles ?? [] }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<{ userId: string; roles: string[] }>({
    userId: '',
    roles: [],
  })
  const [loading, setLoading] = useState(true)

  // Одна гидратация на запуск: до неё запросы к API запрещены (см. http.ts).
  useEffect(() => {
    void hydrateTokens()
      .then((pair) => {
        setSession(claimsOf(pair))
      })
      .finally(() => {
        setLoading(false)
      })
  }, [])

  // Сессия кончилась на любом запросе — сбрасываем состояние здесь.
  useEffect(
    () =>
      onSessionEnded(() => {
        setSession({ userId: '', roles: [] })
      }),
    [],
  )

  const requestCode = useCallback(async (phone: string) => {
    const out = await request('/v1/iam/otp/request', otpRequestReply, {
      method: 'POST',
      body: { phone },
      anonymous: true,
    })
    return out
  }, [])

  const verifyCode = useCallback(async (phone: string, code: string) => {
    const out = await request('/v1/iam/otp/verify', verifyReply, {
      method: 'POST',
      body: { phone, code },
      anonymous: true,
    })
    if (!out.accessToken || !out.refreshToken) {
      throw new ApiError(500, 'NO_TOKENS', 'Сервер не вернул токены')
    }
    const pair: TokenPair = {
      accessToken: out.accessToken,
      refreshToken: out.refreshToken,
      expiresAt: out.expiresAt,
    }
    writeTokens(pair)
    setSession(claimsOf(pair))
  }, [])

  const logout = useCallback(async () => {
    if (readTokens()) {
      // Лучшая попытка отозвать семьи токенов на сервере.
      // Тело НЕ отправляем: public-api игнорирует его и отзывает по `sub`
      // из JWT (public.go:195-197). `{refreshToken: …}`, как шлёт админка, —
      // безобидная, но вводящая в заблуждение выдумка.
      try {
        await request('/v1/iam/logout', z.unknown(), { method: 'POST' })
      } catch {
        /* нет связи — локальный выход всё равно обязан состояться */
      }
    }
    clearTokens()
    // Чужие данные на сменном телефоне остаться не должны.
    await clearOutbox()
    await clearPersistedCache()
    await clearMarks()
    setSession({ userId: '', roles: [] })
  }, [])

  const value = useMemo<AuthState>(
    () => ({
      userId: session.userId,
      roles: session.roles,
      loading,
      isScout: session.roles.includes('scout'),
      isPartner:
        session.roles.includes('partner_scout') && !session.roles.includes('scout'),
      isCollectorOnly: isCollectorOnly(session.roles),
      canCollect: canOpenCollector(session.roles),
      requestCode,
      verifyCode,
      logout,
    }),
    [session, loading, requestCode, verifyCode, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
